import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useGlobal } from '../context/GlobalContext';
import api from '../api/axios';
import { formatProductData } from '../helper/formatProductData';
import toast from 'react-hot-toast';
import { ProductDetailsSkeleton } from './SkeletonLoader';
import ProductImageLightbox from './ProductImageLightbox';

const featureIcons = [
    { icon: 'ph-leaf', label: '100% Natural & Unrefined' },
    { icon: 'ph-heart', label: 'Cholesterol Free' },
    { icon: 'ph-drop', label: 'Rich in Natural Antioxidants' },
    { icon: 'ph-cooking-pot', label: 'Pure & Traditional' },
];

const ProductDetailsOne = () => {
    const location = useLocation();
    const { id, slug } = useParams();
    const productId = slug || id || (location.state?.id || "1");
    const navigate = useNavigate();
    const { addToCart, addToWishlist, removeFromWishlist, wishlistItems } = useGlobal();

    const [productDetails, setProductDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mainImage, setMainImage] = useState("");
    const [reviews, setReviews] = useState([]);

    const [reviewRating, setReviewRating] = useState(0);
    const [reviewContent, setReviewContent] = useState("");
    const [submittingReview, setSubmittingReview] = useState(false);

    useEffect(() => {
        const fetchProductAndReviews = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/products/${productId}`);
                const formatted = formatProductData(response.data);
                setProductDetails(formatted);

                // If visited via legacy UUID, update the browser URL to clean SEO slug
                if (formatted.slug && formatted.slug !== productId) {
                    navigate(`/product-details/${formatted.slug}`, { replace: true });
                }

                if (formatted.rawVariants && formatted.rawVariants.length > 0) {
                    const defaultIdx = (formatted.defaultVariantIndex !== undefined && formatted.defaultVariantIndex !== null)
                        ? formatted.defaultVariantIndex
                        : 0;
                    setSelectedVariantIndex(defaultIdx);
                    const defaultVar = formatted.rawVariants[defaultIdx];
                    if (defaultVar?.images && defaultVar.images.length > 0) {
                        setMainImage(defaultVar.images[0]);
                    } else if (defaultVar?.image) {
                        setMainImage(defaultVar.image);
                    } else {
                        setMainImage(formatted.image);
                    }
                } else {
                    setSelectedVariantIndex(null);
                    setMainImage(formatted.image);
                }

                try {
                    const realId = formatted.id || response.data?.id || productId;
                    const revRes = await api.get(`/api/reviews/product/${realId}`);
                    setReviews(revRes.data);
                } catch (rErr) {
                    console.error("Could not load reviews", rErr);
                }

                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch product details", err);
                setError("Could not load product details.");
                setLoading(false);
            }
        };
        fetchProductAndReviews();
    }, [productId, navigate]);

    // increment & decrement
    const [quantity, setQuantity] = useState(1);
    const incrementQuantity = () => setQuantity(quantity + 1);
    const decrementQuantity = () => setQuantity(quantity > 1 ? quantity - 1 : quantity);
    const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
    const [variantError, setVariantError] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('description');
    const thumbRailRef = useRef(null);

    const scrollThumbs = (direction) => {
        if (thumbRailRef.current) {
            thumbRailRef.current.scrollBy({ top: direction * 110, behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <section className="product-details py-40">
                <div className="container container-lg">
                    <ProductDetailsSkeleton />
                </div>
            </section>
        );
    }

    if (error || !productDetails) {
        return (
            <div className="text-center py-80">
                <i className="ph ph-warning-circle text-4xl text-danger-600 mb-16 d-block" />
                <p className="text-danger-600 mb-16">{error || "Product not found"}</p>
            </div>
        );
    }

    let currentPriceDisplay = productDetails.currentPrice;
    let originalPriceDisplay = productDetails.originalPrice;
    let selectedVariantObj = null;

    const hasVariants = productDetails.rawVariants && productDetails.rawVariants.length > 0;

    if (hasVariants && selectedVariantIndex !== null) {
        selectedVariantObj = productDetails.rawVariants[selectedVariantIndex];
        currentPriceDisplay = `₹${Math.round(selectedVariantObj.salesPrice)}`;
        originalPriceDisplay = `₹${Math.round(selectedVariantObj.mrp)}`;
    } else if (hasVariants) {
        // no variant picked yet - show the first variant's price as a preview,
        // but keep selectedVariantObj null so Add to Cart is blocked
        currentPriceDisplay = `₹${Math.round(productDetails.rawVariants[0].salesPrice)}`;
        originalPriceDisplay = `₹${Math.round(productDetails.rawVariants[0].mrp)}`;
    }

    const handleVariantClick = (i, v) => {
        setSelectedVariantIndex(i);
        setVariantError(false);
        if (v.images && v.images.length > 0) {
            setMainImage(v.images[0]);
        } else if (v.image) {
            setMainImage(v.image);
        }
    };

    const handleAddToCart = () => {
        if (hasVariants && selectedVariantObj === null) {
            setVariantError(true);
            return;
        }
        addToCart(productDetails, quantity, selectedVariantObj);
    };

    const handleBuyNow = () => {
        if (hasVariants && selectedVariantObj === null) {
            setVariantError(true);
            return;
        }
        const buyNowItem = {
            ...productDetails,
            product: productDetails,
            variant: selectedVariantObj,
            selectedVariant: selectedVariantObj,
            selectedVariantId: selectedVariantObj?.id || null,
            shippingWeight: selectedVariantObj?.shippingWeight ?? productDetails.shippingWeight ?? null,
            shippingDimensions: selectedVariantObj?.shippingDimensions || productDetails.shippingDimensions || null,
            quantity: quantity,
        };

        const token = localStorage.getItem('token');
        if (!token) {
            // Ensure item is saved to guest cart and redirect to login
            addToCart(productDetails, quantity, selectedVariantObj);
            toast("Please login to complete your checkout.", { icon: "🔒" });
            navigate('/login?redirect=/checkout', { state: { from: '/checkout', buyNowItem } });
            return;
        }

        navigate('/checkout', { state: { buyNowItem } });
    };

    let productImages = Array.isArray(productDetails.gallery) && productDetails.gallery.length > 0
        ? productDetails.gallery
        : [productDetails.image];

    if (selectedVariantObj) {
        if (selectedVariantObj.images && selectedVariantObj.images.length > 0) {
            productImages = selectedVariantObj.images;
        } else if (selectedVariantObj.image) {
            productImages = [selectedVariantObj.image];
        }
    }

    const activeStock = hasVariants
        ? (selectedVariantObj ? Number(selectedVariantObj.stock || 0) : productDetails.rawVariants.reduce((sum, v) => sum + Number(v.stock || 0), 0))
        : Number(productDetails.stock || 0);

    const isOutOfStock = activeStock <= 0;
    const isLowStock = activeStock > 0 && activeStock <= 10 && selectedVariantObj !== null;

    // Group variants by their prefix (e.g., Weight, Volume, Size)
    const variantGroups = {};
    if (hasVariants) {
        productDetails.rawVariants.forEach((v, index) => {
            let groupName = 'Attribute';
            let displayValue = v.variantName || v.unit || 'Variant ' + (index + 1);
            if (v.variantName && v.variantName.includes(':')) {
                const parts = v.variantName.split(':');
                groupName = parts[0].trim();
                displayValue = parts[1].trim();
            }
            if (!variantGroups[groupName]) {
                variantGroups[groupName] = [];
            }
            variantGroups[groupName].push({ ...v, originalIndex: index, displayValue });
        });
    }

    return (

        <section className="product-details py-40">
            {/* Scoped styles */}
            <style>{`
                .pd-breadcrumb { font-size: 13px; color: #8A8A8A; }
                .pd-main-image-wrap { max-width: 460px; margin: 0 auto; }
                .pd-title { font-size: 26px; line-height: 1.3; font-weight: 700; }
                .pd-price { font-size: 26px; font-weight: 700; }
                .pd-desc { font-size: 14px; line-height: 1.7; max-width: 460px; }
                .pd-breadcrumb a { color: #8A8A8A; }
                .pd-breadcrumb a:hover { color: var(--main-600, #2FA84F); }
                .pd-breadcrumb .current { color: #4B4B4B; font-weight: 500; }
                .pd-scroll-btn {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    color: #9AA0A6;
                    padding: 4px 0;
                    background: none;
                    border: none;
                }
                .pd-scroll-btn:hover { color: var(--main-600, #2FA84F); }
                .pd-thumb-rail::-webkit-scrollbar { width: 4px; height: 4px; }
                .pd-thumb-rail::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 4px; }
                .pd-stock-pill {
                    background: var(--main-50, #E7F7EC);
                    color: var(--main-600, #2FA84F);
                    width: fit-content !important;
                    display: inline-flex !important;
                }
                .pd-feature-box {
                    border: 1px solid #EFEFEF;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .pd-feature-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    color: #4B4B4B;
                }
                .pd-feature-item i { font-size: 18px; color: var(--main-600, #2FA84F); }
                .pd-tabs {
                    display: flex;
                    align-items: center;
                    gap: 40px;
                    border-bottom: 1px solid #EFEFEF;
                }
                .pd-tab-btn {
                    background: none;
                    border: none;
                    padding: 18px 0;
                    font-weight: 600;
                    font-size: 16px;
                    color: #8A8A8A;
                    position: relative;
                }
                .pd-tab-btn.active { color: var(--main-600, #2FA84F); }
                .pd-tab-btn.active::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: -1px;
                    height: 2px;
                    background: var(--main-600, #2FA84F);
                }

                @media (max-width: 767px) {
                    .pd-main-image-wrap {
                        flex-direction: column !important;
                        align-items: center !important;
                        max-width: 100% !important;
                    }
                    .pd-thumb-rail-container {
                        width: 100% !important;
                        flex-direction: row !important;
                        justify-content: center !important;
                        margin-top: 12px !important;
                        order: 2 !important;
                    }
                    .pd-thumb-rail {
                        flex-direction: row !important;
                        overflow-x: auto !important;
                        max-height: none !important;
                        max-width: 100% !important;
                        padding-bottom: 4px;
                    }
                    .pd-scroll-btn {
                        display: none !important;
                    }
                    .pd-main-preview {
                        order: 1 !important;
                        width: 100% !important;
                        max-width: 340px !important;
                    }
                    .pd-title {
                        font-size: 20px !important;
                    }
                    .pd-price {
                        font-size: 22px !important;
                    }
                    .pd-tabs {
                        gap: 20px !important;
                    }
                    .pd-tab-btn {
                        font-size: 14px !important;
                        padding: 12px 0 !important;
                    }
                }

                /* Variant pill buttons */
                .pd-variant-label {
                    font-size: 13px;
                    font-weight: 700;
                    letter-spacing: 0.6px;
                    text-transform: uppercase;
                    color: #6B7280;
                    display: block;
                    margin-bottom: 12px;
                }
                .pd-variant-btn {
                    border: 1.5px solid #E5E7EB;
                    border-radius: 999px;
                    padding: 12px 20px;
                    font-weight: 600;
                    font-size: 14px;
                    background: #fff;
                    color: #333;
                    transition: all 0.2s ease;
                    line-height: 1.2;
                }
                .pd-variant-btn:hover {
                    border-color: var(--main-600, #2FA84F);
                    color: var(--main-600, #2FA84F);
                }
                .pd-variant-btn.active {
                    border-color: var(--main-600, #2FA84F);
                    background: var(--main-50, #E7F7EC);
                    color: var(--main-600, #2FA84F);
                }
                .pd-variant-error {
                    color: #DC2626;
                    font-size: 13px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* Stock alert */
                .pd-stock-alert {
                    background: #FFF4E5;
                    color: #B45309;
                    border-radius: 10px;
                    padding: 12px 16px;
                    font-weight: 600;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
            `}</style>

            <div className="container container-lg">
                {/* Breadcrumb */}
                <nav className="pd-breadcrumb flex-align gap-8 flex-wrap mb-20">
                    <Link to="/">Home</Link>
                    <i className="ph ph-caret-right text-xs" />
                    <Link to="/shop">Shop</Link>
                    <i className="ph ph-caret-right text-xs" />
                    <span>{productDetails.category}</span>
                    <i className="ph ph-caret-right text-xs" />
                    <span className="current">{productDetails.name}</span>
                </nav>

                <div className="row gy-4">
                    <div className="col-xl-5 col-lg-6">
                        <div className="product-details__left d-flex gap-12 pd-main-image-wrap">
                            {/* Vertical thumbnail rail (Horizontal on mobile) */}
                            <div className="pd-thumb-rail-container d-flex flex-column flex-shrink-0 order-2 order-lg-1" style={{ width: '76px' }}>
                                <button type="button" className="pd-scroll-btn" onClick={() => scrollThumbs(-1)} aria-label="Scroll thumbnails up">
                                    <i className="ph ph-caret-up" />
                                </button>
                                <div
                                    ref={thumbRailRef}
                                    className="pd-thumb-rail d-flex flex-column gap-8"
                                    style={{ maxHeight: '340px', overflowY: 'auto' }}
                                >
                                    {productImages.map((image, index) => (
                                        <button
                                            type="button"
                                            key={index}
                                            onClick={() => setMainImage(image)}
                                            className={`border rounded-12 p-4 flex-center flex-shrink-0 transition-2 ${mainImage === image ? 'border-main-600' : 'border-gray-100'}`}
                                            style={{ width: '76px', height: '76px' }}
                                            aria-label={`View image ${index + 1}`}
                                        >
                                            <img
                                                src={image}
                                                alt={`${productDetails.name} thumbnail ${index + 1}`}
                                                className="w-100 h-100 object-fit-contain"
                                            />
                                        </button>
                                    ))}
                                </div>
                                <button type="button" className="pd-scroll-btn" onClick={() => scrollThumbs(1)} aria-label="Scroll thumbnails down">
                                    <i className="ph ph-caret-down" />
                                </button>
                            </div>

                            {/* Main image */}
                            <div
                                className="pd-main-preview position-relative border border-gray-100 rounded-16 flex-center flex-grow-1 order-1 order-lg-2 bg-white"
                                style={{ aspectRatio: '1 / 1', maxWidth: '360px' }}
                            >
                                <img
                                    src={mainImage}
                                    alt={productDetails.name}
                                    className="w-100 h-100 object-fit-contain p-16"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setIsLightboxOpen(true)}
                                    title="Click to view full screen"
                                />
                                <button
                                    type="button"
                                    onClick={() => setIsLightboxOpen(true)}
                                    aria-label="View full screen image"
                                    title="View full screen"
                                    className="position-absolute top-0 end-0 m-12 w-36 h-36 bg-white border border-gray-100 rounded-8 flex-center text-gray-700 hover-bg-main-600 hover-text-white transition-2 shadow-sm"
                                    style={{ zIndex: 2 }}
                                >
                                    <i className="ph ph-arrows-out text-lg" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-7 col-lg-6">
                        <div className="product-details__content">
                            <h3 className="pd-title mb-10">{productDetails.name}</h3>

                            <div className="flex-align gap-10 mb-16">
                                <h3 className="pd-price mb-0 text-main-600">{currentPriceDisplay}</h3>
                                <span className="text-md text-gray-400 text-decoration-line-through">
                                    {originalPriceDisplay}
                                </span>
                            </div>

                            {/* Variant / Size selector */}
                            {hasVariants && Object.keys(variantGroups).map(groupName => (
                                <div className="mb-24" key={groupName}>
                                    <span className="pd-variant-label text-uppercase mb-12 d-block text-gray-700 fw-bold">
                                        Select {groupName}
                                        {selectedVariantObj && variantGroups[groupName].some(v => v.id === selectedVariantObj.id) && (
                                            <span style={{ color: 'var(--main-600, #2FA84F)', fontWeight: 700, textTransform: 'none' }}>
                                                {' '}— {variantGroups[groupName].find(v => v.id === selectedVariantObj.id).displayValue}
                                            </span>
                                        )}
                                    </span>
                                    <div className="d-flex flex-wrap gap-12">
                                        {variantGroups[groupName].map(v => (
                                            <button
                                                key={v.id}
                                                type="button"
                                                onClick={() => handleVariantClick(v.originalIndex, v)}
                                                className={`pd-variant-btn ${selectedVariantIndex === v.originalIndex ? 'active' : ''}`}
                                            >
                                                {v.displayValue}
                                            </button>
                                        ))}
                                    </div>
                                    {/* Variant error message below the last group */}
                                    {variantError && Object.keys(variantGroups)[Object.keys(variantGroups).length - 1] === groupName && (
                                        <div className="pd-variant-error mt-10">
                                            <i className="ph-fill ph-warning-circle" /> Please select a size before adding to cart
                                        </div>
                                    )}
                                </div>
                            ))}

                            {productDetails.rawModel?.shortDescription ? (
                                <div
                                    className="pd-desc text-gray-700 mb-0"
                                    dangerouslySetInnerHTML={{ __html: productDetails.rawModel.shortDescription }}
                                />
                            ) : (
                                <p className="pd-desc text-gray-700 mb-0">
                                    This premium quality product from {productDetails.seller || 'Healthy Home Foods'} is designed to provide you with the best nutritional value. Enjoy the exquisite taste and natural goodness of our carefully curated selection.
                                </p>
                            )}

                            <span className="border-top border-gray-100 d-block mt-20 mb-20" />

                            {isOutOfStock ? (
                                <div className="pd-stock-pill fw-semibold rounded-8 py-10 px-14 flex-align gap-8" style={{ background: '#FEE2E2', color: '#DC2626', width: 'fit-content' }}>
                                    <i className="ph-fill ph-x-circle text-lg" />
                                    Out of Stock
                                </div>
                            ) : (
                                <div className="pd-stock-pill fw-semibold rounded-8 py-10 px-14 flex-align gap-8">
                                    <i className="ph-fill ph-check-circle text-lg" />
                                    In Stock
                                </div>
                            )}

                            {isLowStock && (
                                <div className="pd-stock-alert mt-12">
                                    <i className="ph-fill ph-lightning" />
                                    Only {activeStock} left — selling fast!
                                </div>
                            )}

                            {isOutOfStock && (
                                <div className="pd-stock-error mt-12" style={{ color: '#DC2626', fontSize: '14px', fontWeight: 600 }}>
                                    This item is currently out of stock and cannot be added to your cart.
                                </div>
                            )}

                            <div className="flex-align flex-wrap gap-12 mt-20">
                                <div className="border border-gray-100 rounded-12 py-6 px-12 flex-align flex-shrink-0">
                                    <button
                                        onClick={decrementQuantity}
                                        type="button"
                                        className="quantity__minus p-4 text-gray-700 hover-text-main-600 flex-center"
                                    >
                                        <i className="ph ph-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        className="quantity__input border-0 text-center w-32"
                                        value={quantity} readOnly
                                    />
                                    <button
                                        onClick={incrementQuantity}
                                        type="button"
                                        className="quantity__plus p-4 text-gray-700 hover-text-main-600 flex-center"
                                    >
                                        <i className="ph ph-plus" />
                                    </button>
                                </div>

                                <button
                                    onClick={handleAddToCart}
                                    type="button"
                                    disabled={isOutOfStock}
                                    className={`btn btn-main rounded-12 flex-align d-inline-flex gap-8 px-20 py-10 flex-grow-1 justify-content-center ${isOutOfStock ? 'opacity-50' : ''}`}
                                    style={{ minWidth: '140px', cursor: isOutOfStock ? 'not-allowed' : 'pointer' }}
                                >
                                    <i className="ph ph-shopping-cart" /> {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                                </button>

                                <button
                                    onClick={handleBuyNow}
                                    type="button"
                                    disabled={isOutOfStock}
                                    className={`btn rounded-12 flex-align d-inline-flex gap-8 px-20 py-10 flex-grow-1 justify-content-center border border-main-600 text-main-600 bg-white hover-bg-main-600 hover-text-white transition-2 ${isOutOfStock ? 'opacity-50' : ''}`}
                                    style={{ minWidth: '140px', cursor: isOutOfStock ? 'not-allowed' : 'pointer' }}
                                >
                                    <i className="ph ph-arrow-right" /> Buy Now
                                </button>

                                {(() => {
                                    const currentVariantId = selectedVariantObj?.id || null;
                                    const isWishlisted = wishlistItems?.some(item => 
                                        String(item.id) === String(productDetails.id) && 
                                        (currentVariantId ? String(item.variantId) === String(currentVariantId) : !item.variantId)
                                    );
                                    return (
                                        <button
                                            onClick={() => {
                                                if (isWishlisted) {
                                                    removeFromWishlist(productDetails.id, currentVariantId);
                                                } else {
                                                    addToWishlist(productDetails, selectedVariantObj);
                                                }
                                            }}
                                            type="button"
                                            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                                            title={isWishlisted ? "In Wishlist (Click to remove)" : "Add to Wishlist"}
                                            className={`w-44 h-44 border rounded-12 flex-center flex-shrink-0 transition-2 ${
                                                isWishlisted
                                                    ? 'bg-danger-50 border-danger-200 text-danger-600'
                                                    : 'border-gray-100 text-gray-700 hover-bg-main-600 hover-text-white bg-transparent'
                                            }`}
                                        >
                                            <i className={`${isWishlisted ? 'ph-fill ph-heart text-danger-600' : 'ph ph-heart'} text-xl`} />
                                        </button>
                                    );
                                })()}
                            </div>

                            {/* Feature icons row */}
                            <div className="pd-feature-box mt-20">
                                <div className="row g-3">
                                    {featureIcons.map((f, idx) => (
                                        <div className="col-6 col-sm-3" key={idx}>
                                            <div className="pd-feature-item">
                                                <i className={`ph ${f.icon}`} />
                                                <span>{f.label}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-40">
                    {/* Tabs */}
                    <div className="pd-tabs mb-32">
                        <button
                            type="button"
                            className={`pd-tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                            onClick={() => setActiveTab('description')}
                        >
                            Description
                        </button>
                        <button
                            type="button"
                            className={`pd-tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                            onClick={() => setActiveTab('reviews')}
                        >
                            Reviews ({reviews.length})
                        </button>
                    </div>

                    {/* Description Tab */}
                    {activeTab === 'description' && (
                        <div>
                            {productDetails.rawModel?.fullDescription ? (
                                <div
                                    className="mb-40"
                                    style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}
                                    dangerouslySetInnerHTML={{ __html: productDetails.rawModel.fullDescription }}
                                />
                            ) : (
                                <div className="mb-40">
                                    <h6 className="mb-24">Product Description</h6>
                                    <p>
                                        Experience the authentic taste and health benefits of {productDetails.name}. Sourced from the finest ingredients, this product represents our commitment to quality, purity, and traditional practices.
                                    </p>
                                    <p>
                                        Whether you are cooking a delicious family meal or looking for premium healthy alternatives, this product seamlessly integrates into your daily life. Enjoy the assurance of 100% natural processing with no added artificial preservatives.
                                    </p>
                                    <ul className="list-inside mt-32 ms-16">
                                        <li className="text-gray-400 mb-4">
                                            Premium quality {productDetails.category || 'product'}
                                        </li>
                                        <li className="text-gray-400 mb-4">
                                            100% Natural and pure ingredients
                                        </li>
                                        <li className="text-gray-400 mb-4">
                                            Maintains essential nutrients and natural aroma
                                        </li>
                                        <li className="text-gray-400 mb-4">Chemical-free processing</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}


                    {/* Reviews Tab */}
                    {activeTab === 'reviews' && (
                        <div className="row g-4">
                            <div className="col-lg-6">
                                <h6 className="mb-24">Customer Reviews</h6>
                                {reviews.length === 0 && <p className="text-gray-500">No reviews yet for this product.</p>}
                                {reviews.map((r, idx) => {
                                    const authorName = r.Customer?.name || r.guestName || "User";
                                    const initials = authorName.trim().substring(0, 2).toUpperCase() || "U";
                                    const dateObj = new Date(r.createdAt);
                                    const displayDate = isNaN(dateObj) ? '' : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                                    return (
                                        <div
                                            className={`d-flex align-items-start gap-24 ${idx < reviews.length - 1 ? 'pb-44 border-bottom border-gray-100 mb-44' : ''}`}
                                            key={r.id}
                                        >
                                            {/* Dynamic Avatar instead of Image */}
                                            <div className="w-52 h-52 rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center" style={{ background: '#E7F7EC', color: '#2FA84F', fontSize: '20px', fontWeight: 'bold' }}>
                                                {initials}
                                            </div>

                                            <div className="flex-grow-1">
                                                <div className="flex-between align-items-start gap-8 ">
                                                    <h6 className="mb-2 text-md">{authorName}</h6>
                                                    <span className="text-gray-800 text-xs">{displayDate}</span>
                                                </div>
                                                <div className="flex-align gap-4 mb-14" style={{ marginTop: '4px' }}>
                                                    {[...Array(5)].map((_, i) => (
                                                        <span key={i} style={{ color: i < Math.round(r.rating || 5) ? '#f59e0b' : '#d1d5db', fontSize: '16px', lineHeight: 1 }}>
                                                            ★
                                                        </span>
                                                    ))}
                                                </div>
                                                <p className="text-gray-700">{r.feedback}</p>
                                            </div>
                                        </div>
                                    )
                                })}

                                {/* <div className="mt-56">
                                    <h6 className="mb-24">Write a Review</h6>
                                    <span className="text-heading mb-8">
                                        What is it like to use this product?
                                    </span>
                                    <div className="mt-32">
                                        <form onSubmit={async (e) => {
                                            e.preventDefault();
                                            if (!localStorage.getItem('token')) {
                                                toast.error('Please login to write a review');
                                                navigate('/login');
                                                return;
                                            }
                                            if (reviewRating === 0) {
                                                toast.error('Please select a star rating');
                                                return;
                                            }
                                            if (!reviewContent.trim()) {
                                                toast.error('Please write some review content');
                                                return;
                                            }

                                            setSubmittingReview(true);
                                            try {
                                                const res = await api.post('/api/reviews', {
                                                    productId: productId,
                                                    feedback: reviewContent,
                                                    rating: reviewRating
                                                });
                                                toast.success('Review submitted successfully! It will be visible after admin approval.');
                                                setReviewContent('');
                                                setReviewRating(0);
                                            } catch (err) {
                                                console.error(err);
                                                toast.error(err.response?.data?.message || 'Failed to submit review');
                                            } finally {
                                                setSubmittingReview(false);
                                            }
                                        }}>
                                            <div className="mb-32">
                                                <label className="text-neutral-600 mb-8 d-block">
                                                    Your Rating
                                                </label>
                                                <div className="d-flex align-items-center gap-8">
                                                    {[1, 2, 3, 4, 5].map((star) => {
                                                        const isFilled = star <= reviewRating;
                                                        return (
                                                            <span
                                                                key={star}
                                                                onClick={() => setReviewRating(reviewRating === star ? 0 : star)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '28px',
                                                                    lineHeight: 1,
                                                                    color: isFilled ? '#f59e0b' : '#d1d5db',
                                                                    transition: 'color 0.2s ease'
                                                                }}
                                                            >
                                                                {isFilled ? '★' : '☆'}
                                                            </span>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                            <div className="mb-32">
                                                <label htmlFor="desc" className="text-neutral-600 mb-8">
                                                    Review Content
                                                </label>
                                                <textarea
                                                    className="common-input rounded-8"
                                                    id="desc"
                                                    placeholder="Share your thoughts about this product..."
                                                    value={reviewContent}
                                                    onChange={(e) => setReviewContent(e.target.value)}
                                                    rows={4}
                                                    required
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-main rounded-pill mt-48"
                                                disabled={submittingReview}
                                            >
                                                {submittingReview ? 'Submitting...' : 'Submit Review'}
                                            </button>
                                        </form>
                                    </div>
                                </div> */}
                            </div>

                            <div className="col-lg-6">
                                <div className="ms-xxl-5">
                                    <h6 className="mb-24">Customers Feedback</h6>
                                    <div className="border border-gray-100 rounded-8 px-24 py-40">
                                        {(() => {
                                            const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
                                            reviews.forEach(r => {
                                                const stars = Math.round(r.rating || 5);
                                                if (counts[stars] !== undefined) counts[stars]++;
                                            });
                                            const total = reviews.length || 1;
                                            const breakdown = [5, 4, 3, 2, 1].map(stars => ({
                                                stars,
                                                count: counts[stars],
                                                percent: Math.round((counts[stars] / total) * 100)
                                            }));

                                            return breakdown.map((r) => (
                                                <div className="flex-align gap-8 mb-20" key={r.stars}>
                                                    <span className="text-gray-900 flex-shrink-0">{r.stars}</span>
                                                    <div
                                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                                        role="progressbar"
                                                        aria-label={`${r.stars} star ratings`}
                                                        aria-valuenow={r.percent}
                                                        aria-valuemin={0}
                                                        aria-valuemax={100}
                                                    >
                                                        <div
                                                            className="progress-bar bg-main-600 rounded-pill"
                                                            style={{ width: `${r.percent}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-gray-900 flex-shrink-0">{r.count}</span>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Fullscreen Product Image Lightbox Modal */}
            <ProductImageLightbox
                isOpen={isLightboxOpen}
                onClose={() => setIsLightboxOpen(false)}
                images={productImages}
                currentIndex={Math.max(0, productImages.indexOf(mainImage))}
                onIndexChange={(newIdx) => {
                    if (productImages[newIdx]) {
                        setMainImage(productImages[newIdx]);
                    }
                }}
                productName={productDetails.name}
            />
        </section>

    )
}

export default ProductDetailsOne